import * as Sentry from '@sentry/react';

type TwilioSignalingEvent = {
  elapsedTime?: number;
  group?: string;
  level?: string;
  name?: string;
  payload?: unknown;
  timestamp?: number;
};

type TwilioDiagnosticContext = {
  roomName?: string | null;
  roomSid?: string | null;
  roomState?: string | null;
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const getCurrentPageTag = (): string => {
  if (typeof window === 'undefined') {
    return 'unknown';
  }

  const path = window.location.pathname.toLowerCase();
  if (path.includes('clientpage')) return 'clientpage';
  if (path.includes('practitionerpage')) return 'practitionerpage';
  return path || 'unknown';
};

const getVisibilityState = (): string | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  return document.visibilityState ?? null;
};

const getOnlineState = (): boolean | null => {
  if (typeof navigator === 'undefined' || typeof navigator.onLine !== 'boolean') {
    return null;
  }

  return navigator.onLine;
};

const getStringValue = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
};

const getNumberValue = (value: unknown): number | null => (typeof value === 'number' ? value : null);

const toError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }

  const message = getStringValue(error);
  return new Error(message ?? 'Twilio diagnostic error');
};

const getErrorCode = (error: unknown): string | null => {
  if (!isRecord(error)) {
    return null;
  }

  const code = error.code;
  if (typeof code === 'number') {
    return String(code);
  }

  return getStringValue(code);
};

const getErrorMessage = (error: unknown): string | null => {
  if (error instanceof Error) {
    return getStringValue(error.message) ?? error.message;
  }

  if (!isRecord(error)) {
    return getStringValue(error);
  }

  return getStringValue(error.message);
};

const getErrorName = (error: unknown): string | null => {
  if (error instanceof Error) {
    return getStringValue(error.name) ?? error.name;
  }

  if (!isRecord(error)) {
    return null;
  }

  return getStringValue(error.name);
};

const getSignalingReason = (payload: unknown): string | null => {
  if (!isRecord(payload)) {
    return null;
  }

  return getStringValue(payload.reason);
};

const MAX_TRACKED_TOKENS = 50;
const connectAttemptCounts = new Map<string, number>();

const hashToken = (token: string): string => {
  let hash = 5381;
  for (let i = 0; i < token.length; i += 1) {
    hash = ((hash << 5) + hash + token.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
};

type TwilioTokenGrantInfo = {
  identity: string | null;
  room: string | null;
  expiresAt: string | null;
};

const decodeBase64Url = (value: string): string | null => {
  if (typeof atob !== 'function') {
    return null;
  }

  try {
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return atob(padded);
  } catch {
    return null;
  }
};

// Reads identity, room grant and expiry from a Twilio access token payload.
// Diagnostics only: the signature is NOT verified, never use this for auth decisions.
export const getTwilioTokenGrantInfo = (token: string | null | undefined): TwilioTokenGrantInfo | null => {
  if (!token) {
    return null;
  }

  const parts = token.split('.');
  if (parts.length < 2) {
    return null;
  }

  const json = decodeBase64Url(parts[1]);
  if (!json) {
    return null;
  }

  try {
    const payload = JSON.parse(json) as unknown;
    if (!isRecord(payload)) {
      return null;
    }
    const grants: Record<string, unknown> = isRecord(payload.grants) ? payload.grants : {};
    const video: Record<string, unknown> = isRecord(grants.video) ? grants.video : {};
    const exp = getNumberValue(payload.exp);

    return {
      identity: getStringValue(grants.identity),
      room: getStringValue(video.room),
      expiresAt: exp !== null ? new Date(exp * 1000).toISOString() : null,
    };
  } catch {
    return null;
  }
};

export const recordTwilioConnectAttempt = (token: string | null | undefined): number => {
  if (!token) {
    return 0;
  }

  const fingerprint = hashToken(token);
  const previous = connectAttemptCounts.get(fingerprint) ?? 0;
  const attempt = previous + 1;
  connectAttemptCounts.set(fingerprint, attempt);

  // Prune oldest entries so the map can't grow without bound on long-lived tabs.
  if (connectAttemptCounts.size > MAX_TRACKED_TOKENS) {
    const firstKey = connectAttemptCounts.keys().next().value;
    if (firstKey !== undefined) {
      connectAttemptCounts.delete(firstKey);
    }
  }

  const grant = getTwilioTokenGrantInfo(token);

  Sentry.addBreadcrumb({
    category: 'twilio.connect',
    level: attempt > 1 ? 'warning' : 'info',
    message: `Video.connect attempt ${attempt} for token ${fingerprint}`,
    data: {
      tokenFingerprint: fingerprint,
      attempt,
      page: getCurrentPageTag(),
      online: getOnlineState(),
      tokenRoom: grant?.room ?? null,
      tokenIdentity: grant?.identity ?? null,
      tokenExpiresAt: grant?.expiresAt ?? null,
    },
  });

  return attempt;
};

export const captureTwilioDuplicateIdentityDiagnostic = (
  error: unknown,
  source: 'room_disconnected' | 'connect_failed',
  context: TwilioDiagnosticContext = {}
): void => {
  const page = getCurrentPageTag();
  const code = getErrorCode(error);
  const errorMessage = getErrorMessage(error);
  const errorName = getErrorName(error);

  Sentry.withScope((scope) => {
    scope.setLevel('warning');
    scope.setFingerprint(['twilio-duplicate-identity', page, source]);
    scope.setTag('diagnostic_source', 'twilio');
    scope.setTag('twilio_diagnostic', 'duplicate_identity');
    scope.setTag('app_page', page);
    scope.setTag('twilio_duplicate_identity_source', source);

    if (code) {
      scope.setTag('twilio_code', code);
    }

    scope.setContext('twilio_duplicate_identity', {
      code,
      errorMessage,
      errorName,
      source,
      roomName: context.roomName ?? null,
      roomSid: context.roomSid ?? null,
      roomState: context.roomState ?? null,
      visibilityState: getVisibilityState(),
      online: getOnlineState(),
    });

    Sentry.captureMessage(`Twilio duplicate identity (${source})`, 'warning');
  });
};

export type TwilioRoomNotFoundSource = 'connect' | 'room_disconnected';

export const captureTwilioRoomNotFoundDiagnostic = (
  error: unknown,
  context: TwilioDiagnosticContext = {},
  token?: string | null,
  source: TwilioRoomNotFoundSource = 'connect'
): void => {
  const page = getCurrentPageTag();
  const code = getErrorCode(error);
  const errorMessage = getErrorMessage(error);
  const errorName = getErrorName(error);
  const tokenFingerprint = token ? hashToken(token) : null;
  const attempt = tokenFingerprint ? (connectAttemptCounts.get(tokenFingerprint) ?? null) : null;
  const grant = getTwilioTokenGrantInfo(token);

  Sentry.withScope((scope) => {
    scope.setLevel('warning');
    // Connect-time failures keep the historical fingerprint so the existing Sentry
    // issue keeps receiving them; disconnect-time failures get their own issue.
    scope.setFingerprint(
      source === 'connect' ? ['twilio-room-not-found', page] : ['twilio-room-not-found', page, source]
    );
    scope.setTag('diagnostic_source', 'twilio');
    scope.setTag('twilio_diagnostic', 'room_not_found');
    scope.setTag('twilio_room_not_found_source', source);
    scope.setTag('app_page', page);

    if (code) {
      scope.setTag('twilio_code', code);
    }

    scope.setContext('twilio_room_not_found', {
      code,
      errorMessage,
      errorName,
      source,
      attempt,
      tokenFingerprint,
      tokenRoom: grant?.room ?? null,
      tokenIdentity: grant?.identity ?? null,
      tokenExpiresAt: grant?.expiresAt ?? null,
      roomName: context.roomName ?? null,
      roomSid: context.roomSid ?? null,
      roomState: context.roomState ?? null,
      visibilityState: getVisibilityState(),
      online: getOnlineState(),
    });

    Sentry.captureMessage(
      source === 'connect' ? 'Twilio room not found' : 'Twilio room not found (room_disconnected)',
      'warning'
    );
  });
};

export const captureTwilioSignalingClosedDiagnostic = (
  event: TwilioSignalingEvent,
  context: TwilioDiagnosticContext = {}
): void => {
  if (event.group !== 'signaling' || event.name !== 'closed' || event.level !== 'error') {
    return;
  }

  const page = getCurrentPageTag();
  const reason = getSignalingReason(event.payload);

  Sentry.withScope((scope) => {
    scope.setLevel('warning');
    scope.setFingerprint(['twilio-signaling-closed', page, reason ?? 'unknown']);
    scope.setTag('diagnostic_source', 'twilio');
    scope.setTag('twilio_diagnostic', 'signaling_closed');
    scope.setTag('app_page', page);

    if (reason) {
      scope.setTag('twilio_close_reason', reason);
    }

    scope.setContext('twilio_signaling_closed', {
      reason,
      elapsedTime: getNumberValue(event.elapsedTime),
      eventTimestamp: getNumberValue(event.timestamp),
      roomName: context.roomName ?? null,
      roomSid: context.roomSid ?? null,
      roomState: context.roomState ?? null,
      visibilityState: getVisibilityState(),
      online: getOnlineState(),
    });

    Sentry.captureMessage(`Twilio signaling closed${reason ? ` (${reason})` : ''}`, 'warning');
  });
};

export const captureTwilioConnectFailureDiagnostic = (error: unknown, context: TwilioDiagnosticContext = {}): void => {
  const page = getCurrentPageTag();
  const exception = toError(error);
  const code = getErrorCode(error);
  const errorMessage = getErrorMessage(error) ?? exception.message;
  const errorName = getErrorName(error) ?? exception.name;

  Sentry.withScope((scope) => {
    scope.setLevel('error');
    scope.setFingerprint(['twilio-connect-failed', page, code ?? 'unknown']);
    scope.setTag('diagnostic_source', 'twilio');
    scope.setTag('twilio_diagnostic', 'connect_failed');
    scope.setTag('app_page', page);

    if (code) {
      scope.setTag('twilio_code', code);
    }

    scope.setContext('twilio_connect_failure', {
      code,
      errorMessage,
      errorName,
      roomName: context.roomName ?? null,
      roomSid: context.roomSid ?? null,
      roomState: context.roomState ?? null,
      visibilityState: getVisibilityState(),
      online: getOnlineState(),
    });

    Sentry.captureException(exception);
  });
};

// Errors delivered with the room's `disconnected` event (after a successful connect).
// Until now these went straight to the global error dialog without any trace in Sentry.
export const captureTwilioRoomDisconnectedDiagnostic = (
  error: unknown,
  context: TwilioDiagnosticContext = {}
): void => {
  const page = getCurrentPageTag();
  const code = getErrorCode(error);
  const errorMessage = getErrorMessage(error);
  const errorName = getErrorName(error);

  Sentry.withScope((scope) => {
    scope.setLevel('warning');
    scope.setFingerprint(['twilio-room-disconnected', page, code ?? 'unknown']);
    scope.setTag('diagnostic_source', 'twilio');
    scope.setTag('twilio_diagnostic', 'room_disconnected');
    scope.setTag('app_page', page);

    if (code) {
      scope.setTag('twilio_code', code);
    }

    scope.setContext('twilio_room_disconnected', {
      code,
      errorMessage,
      errorName,
      roomName: context.roomName ?? null,
      roomSid: context.roomSid ?? null,
      roomState: context.roomState ?? null,
      visibilityState: getVisibilityState(),
      online: getOnlineState(),
    });

    Sentry.captureMessage(
      `Twilio room disconnected${code ? ` (${code})` : ''}${errorMessage ? `: ${errorMessage}` : ''}`,
      'warning'
    );
  });
};
